/**
 * Durable SherlocReports "Run Analysis" operation client.
 *
 * Shared byte-identically by Cortex (client) and Quantum (superadmin) —
 * one canonical backend operation contract, one lifecycle behavior.
 *
 * Born from the 2026-08-16 Never Stop Chasing incident: the synchronous
 * POST /api/ai/run outlived every HTTP transport window once governed
 * review made runs multi-minute, so three clicks created three invisible
 * backend runs (ai_runs 1040/1041/1042) and the page stayed on a false
 * "Running Sherloc AI" label with no operation identity, elapsed time,
 * heartbeat, terminal disposition, or retry guidance.
 *
 * This module drives the durable lifecycle instead:
 *   POST /api/ai/run-operations/:advertiserId  → create-or-reuse ONE
 *     operation per advertiser (identical inputs reuse the active
 *     operation; identical completed inputs replay without regenerating;
 *     conflicting inputs reconnect to the active operation via 409);
 *   GET  /api/ai/run-operations/operation/:id  → poll until terminal;
 *   GET  /api/ai/run-operations/:advertiserId/active → reload recovery;
 *   POST /api/ai/run-operations/operation/:id/retry → governed Safe Retry.
 *
 * There is deliberately NO client-side generation timeout — the operation
 * row is the single source of truth, and the page derives its state from
 * the operation, never from component-local fetch state.
 */

export const SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH = "/api/ai/run-operations";
export const SHERLOC_RUN_ANALYSIS_POLL_INTERVAL_MS = 4000;
export const SHERLOC_RUN_ANALYSIS_ELAPSED_TICK_MS = 1000;

export const SHERLOC_RUN_ANALYSIS_STATUSES = Object.freeze({
  QUEUED: "QUEUED",
  GENERATING: "GENERATING",
  VALIDATING: "VALIDATING",
  PERSISTING: "PERSISTING",
  COMPLETED: "COMPLETED",
  FAILED_RETRYABLE: "FAILED_RETRYABLE",
  FAILED_TERMINAL: "FAILED_TERMINAL",
  SUPERSEDED: "SUPERSEDED",
  CANCELLED: "CANCELLED",
});

export const SHERLOC_RUN_ANALYSIS_ACTIVE_STATUSES = Object.freeze([
  SHERLOC_RUN_ANALYSIS_STATUSES.QUEUED,
  SHERLOC_RUN_ANALYSIS_STATUSES.GENERATING,
  SHERLOC_RUN_ANALYSIS_STATUSES.VALIDATING,
  SHERLOC_RUN_ANALYSIS_STATUSES.PERSISTING,
]);

// Stage labels for the operator card (CAPS applied by the display font, so
// copy is written in sentence case). GENERATING prefers the operation's own
// persisted progress label — governed review reports "Reviewing for
// accuracy — cycle X of Y" through it.
export const SHERLOC_RUN_ANALYSIS_STAGE_LABELS = Object.freeze({
  [SHERLOC_RUN_ANALYSIS_STATUSES.QUEUED]: "Queued",
  [SHERLOC_RUN_ANALYSIS_STATUSES.GENERATING]: "Generating analysis narrative",
  [SHERLOC_RUN_ANALYSIS_STATUSES.VALIDATING]: "Reviewing for accuracy",
  [SHERLOC_RUN_ANALYSIS_STATUSES.PERSISTING]: "Saving analysis",
});

export const SHERLOC_RUN_ANALYSIS_TERMINAL_LABELS = Object.freeze({
  [SHERLOC_RUN_ANALYSIS_STATUSES.COMPLETED]: "Analysis completed",
  [SHERLOC_RUN_ANALYSIS_STATUSES.FAILED_RETRYABLE]:
    "Analysis failed — safe retry available",
  [SHERLOC_RUN_ANALYSIS_STATUSES.FAILED_TERMINAL]:
    "Analysis could not complete",
  [SHERLOC_RUN_ANALYSIS_STATUSES.SUPERSEDED]:
    "This analysis was superseded by a newer request",
  [SHERLOC_RUN_ANALYSIS_STATUSES.CANCELLED]: "This analysis was cancelled",
});

export const SHERLOC_RUN_ANALYSIS_UNKNOWN_STATE_LABEL =
  "Analysis status unavailable";

export const SHERLOC_RUN_ANALYSIS_CONTINUITY_COPY =
  "You can leave or reload this page — the analysis continues on the server and will be here when you return.";

export const SHERLOC_RUN_ANALYSIS_DISPOSITION_COPY = Object.freeze({
  FAILED_RETRYABLE:
    "Sherloc hit a temporary provider problem. The selected campaign and timeframe are preserved — Safe Retry runs the same analysis again.",
  FAILED_TERMINAL:
    "Sherloc could not complete this analysis, and an identical retry would fail the same way. The campaign and timeframe are preserved; support has the reference below.",
  REJECTED_REGENERATION_REQUIRED:
    "Independent governance review declined this analysis narrative. The delivery data on this page is unaffected — run a fresh analysis to regenerate it.",
  SUPERSEDED:
    "A newer analysis request replaced this one. The newest operation carries the result.",
  CANCELLED: "This analysis was cancelled before it finished.",
});

const trimBase = (value) => String(value || "").replace(/\/+$/, "");
const readString = (value) => String(value ?? "").trim();

export const normalizeRunAnalysisStatus = (value) =>
  readString(value).toUpperCase();

export const isRunAnalysisOperationActive = (operation) =>
  SHERLOC_RUN_ANALYSIS_ACTIVE_STATUSES.includes(
    normalizeRunAnalysisStatus(operation?.status)
  );

export const isRunAnalysisOperationTerminal = (operation) => {
  const status = normalizeRunAnalysisStatus(operation?.status);
  return (
    Boolean(status) && !SHERLOC_RUN_ANALYSIS_ACTIVE_STATUSES.includes(status)
  );
};

export const resolveRunAnalysisDisposition = (operation) => {
  const declared = readString(operation?.disposition).toUpperCase();
  if (declared) return declared;
  const status = normalizeRunAnalysisStatus(operation?.status);
  if (!status) return null;
  if (SHERLOC_RUN_ANALYSIS_ACTIVE_STATUSES.includes(status)) return "IN_PROGRESS";
  if (status === SHERLOC_RUN_ANALYSIS_STATUSES.FAILED_TERMINAL) {
    const failureClass = readString(operation?.failureClass).toLowerCase();
    return ["ai_governance_rejected", "governance_rejected"].includes(failureClass)
      ? "REJECTED_REGENERATION_REQUIRED"
      : "FAILED_TERMINAL";
  }
  return status;
};

/**
 * The operator card's stage line. Governed progress ("Reviewing for
 * accuracy — cycle X of Y") wins over the coarse status label whenever the
 * operation carries it.
 */
export const getRunAnalysisStageLabel = (operation) => {
  const status = normalizeRunAnalysisStatus(operation?.status);
  const progressLabel = readString(operation?.generation?.progress?.label);
  if (
    progressLabel &&
    SHERLOC_RUN_ANALYSIS_ACTIVE_STATUSES.includes(status) &&
    status !== SHERLOC_RUN_ANALYSIS_STATUSES.QUEUED
  ) {
    return progressLabel;
  }
  return (
    SHERLOC_RUN_ANALYSIS_STAGE_LABELS[status] ||
    SHERLOC_RUN_ANALYSIS_TERMINAL_LABELS[status] ||
    SHERLOC_RUN_ANALYSIS_UNKNOWN_STATE_LABEL
  );
};

export const getRunAnalysisCycleLabel = (operation) => {
  const completed = Number(operation?.generation?.progress?.completedStages);
  const total = Number(operation?.generation?.progress?.totalStages);
  if (!Number.isFinite(completed) || !Number.isFinite(total) || total <= 0) {
    return "";
  }
  return `Cycle ${completed} of ${total}`;
};

export const getRunAnalysisSupportReference = (operation) => {
  const declared = readString(operation?.supportReference);
  if (declared) return declared.toUpperCase();
  const id = readString(operation?.id);
  return id ? id.slice(0, 8).toUpperCase() : "";
};

const parseTimestamp = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getRunAnalysisElapsedMs = (operation, now = Date.now()) => {
  const startedAt =
    parseTimestamp(operation?.startedAt) || parseTimestamp(operation?.createdAt);
  if (startedAt === null) return null;
  const endedAt =
    parseTimestamp(operation?.completedAt) ||
    parseTimestamp(operation?.failedAt) ||
    now;
  return Math.max(0, endedAt - startedAt);
};

export const formatRunAnalysisElapsed = (elapsedMs) => {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return "";
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const formatRunAnalysisHeartbeat = (operation, now = Date.now()) => {
  const heartbeatAt =
    parseTimestamp(operation?.lastHeartbeatAt) ||
    parseTimestamp(operation?.updatedAt);
  if (heartbeatAt === null) return "";
  const ageSeconds = Math.max(0, Math.floor((now - heartbeatAt) / 1000));
  if (ageSeconds < 5) return "Updated just now";
  if (ageSeconds < 60) return `Updated ${ageSeconds}s ago`;
  return `Updated ${Math.floor(ageSeconds / 60)}m ago`;
};

export const getRunAnalysisMaxAttempts = (operation) => {
  const declared = Number(operation?.maxAttempts);
  return Number.isFinite(declared) && declared > 0 ? declared : 3;
};

export const getRunAnalysisRetryAfterSeconds = (operation) => {
  const declared = Number(operation?.retryAfterSeconds);
  return Number.isFinite(declared) && declared > 0 ? declared : 0;
};

/**
 * Safe Retry is offered ONLY for a genuinely retryable failure: status
 * FAILED_RETRYABLE, the row itself not marked non-retryable, attempts left
 * in the governed budget, and any provider backoff elapsed.
 */
export const isRunAnalysisRetryAvailable = (operation) => {
  if (
    normalizeRunAnalysisStatus(operation?.status) !==
    SHERLOC_RUN_ANALYSIS_STATUSES.FAILED_RETRYABLE
  ) {
    return false;
  }
  if (operation?.retryable === false) return false;
  if (Number(operation?.attemptCount || 0) >= getRunAnalysisMaxAttempts(operation)) {
    return false;
  }
  return getRunAnalysisRetryAfterSeconds(operation) === 0;
};

export const describeRunAnalysisTerminalFailure = (operation) => {
  const disposition = resolveRunAnalysisDisposition(operation);
  const copy = SHERLOC_RUN_ANALYSIS_DISPOSITION_COPY[disposition];
  if (copy) return copy;
  return SHERLOC_RUN_ANALYSIS_DISPOSITION_COPY.FAILED_TERMINAL;
};

/**
 * Scope guard: a completed operation's output may render ONLY for the
 * context that requested it. Campaign selection, timeframe, and engine must
 * all match — a stale or cross-campaign result is refused, never rendered.
 */
export const runAnalysisResultMatchesContext = (operation, expected = {}) => {
  const context = operation?.result?.analysisContext;
  if (!context || typeof context !== "object") return false;
  const norm = (value) => {
    const s = readString(value);
    return s && s !== "auto" ? s : "";
  };
  if (readString(context.advertiserId) !== readString(expected.advertiserId)) {
    return false;
  }
  if (norm(context.campaignId) !== norm(expected.campaignId)) return false;
  if (readString(context.date_start) !== readString(expected.date_start)) {
    return false;
  }
  if (readString(context.date_end) !== readString(expected.date_end)) return false;
  if (readString(context.date_preset) !== readString(expected.date_preset)) {
    return false;
  }
  return true;
};

const readJsonSafely = async (response) => {
  const raw = await response.text().catch(() => "");
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { __malformed: true };
  }
};

const buildRequestError = (response, payload = {}) => {
  const error = new Error(
    readString(payload?.error || payload?.message) ||
      `Sherloc analysis operation request failed (${response.status}).`
  );
  error.status = response.status;
  error.code = readString(payload?.code) || null;
  error.operation = payload?.operation || null;
  error.retryAfterSeconds =
    Number.isFinite(Number(payload?.retryAfterSeconds)) &&
    Number(payload?.retryAfterSeconds) > 0
      ? Number(payload.retryAfterSeconds)
      : null;
  error.payload = payload;
  return error;
};

const operationRequest = async ({
  apiBase,
  getAuthHeader,
  path,
  method = "GET",
  body,
  signal,
  fetchImpl,
}) => {
  const doFetch = fetchImpl || fetch;
  const response = await doFetch(`${trimBase(apiBase)}${path}`, {
    method,
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(typeof getAuthHeader === "function" ? getAuthHeader() : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });
  const payload = await readJsonSafely(response);
  return { response, payload };
};

/**
 * POST /:advertiserId — create or reuse the durable analysis operation.
 * Resolves to { outcome, operation } with outcome one of:
 *   created | reused | completed_replay | in_progress
 * Anything else throws a typed error.
 */
export const startRunAnalysisOperation = async ({
  apiBase,
  getAuthHeader,
  advertiserId,
  payload,
  signal,
  fetchImpl,
} = {}) => {
  const { response, payload: body } = await operationRequest({
    apiBase,
    getAuthHeader,
    path: `${SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH}/${encodeURIComponent(advertiserId)}`,
    method: "POST",
    body: payload,
    signal,
    fetchImpl,
  });
  const operation = body?.operation || null;
  if (response.status === 201) return { outcome: "created", operation };
  if (response.ok && body?.completedReplay) {
    return { outcome: "completed_replay", operation };
  }
  if (response.ok && body?.reused) return { outcome: "reused", operation };
  if (response.ok) return { outcome: "created", operation };
  if (response.status === 409 && body?.code === "operation_in_progress" && operation) {
    return { outcome: "in_progress", operation };
  }
  throw buildRequestError(response, body);
};

export const getRunAnalysisOperation = async ({
  apiBase,
  getAuthHeader,
  operationId,
  signal,
  fetchImpl,
} = {}) => {
  const { response, payload } = await operationRequest({
    apiBase,
    getAuthHeader,
    path: `${SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH}/operation/${encodeURIComponent(operationId)}`,
    signal,
    fetchImpl,
  });
  if (!response.ok) throw buildRequestError(response, payload);
  return { operation: payload?.operation || null };
};

export const getActiveRunAnalysisOperation = async ({
  apiBase,
  getAuthHeader,
  advertiserId,
  includeTerminal = false,
  signal,
  fetchImpl,
} = {}) => {
  const suffix = includeTerminal ? "?active=0" : "";
  const { response, payload } = await operationRequest({
    apiBase,
    getAuthHeader,
    path: `${SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH}/${encodeURIComponent(advertiserId)}/active${suffix}`,
    signal,
    fetchImpl,
  });
  if (!response.ok) throw buildRequestError(response, payload);
  return { operation: payload?.operation || null };
};

export const retryRunAnalysisOperation = async ({
  apiBase,
  getAuthHeader,
  operationId,
  signal,
  fetchImpl,
} = {}) => {
  const { response, payload } = await operationRequest({
    apiBase,
    getAuthHeader,
    path: `${SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH}/operation/${encodeURIComponent(operationId)}/retry`,
    method: "POST",
    signal,
    fetchImpl,
  });
  if (!response.ok) throw buildRequestError(response, payload);
  return { operation: payload?.operation || null };
};

const buildAbortError = () => {
  const error = new Error("The Sherloc analysis request was aborted.");
  error.name = "AbortError";
  return error;
};

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (signal) signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(buildAbortError());
    };
    if (signal) {
      if (signal.aborted) {
        clearTimeout(timer);
        reject(buildAbortError());
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });

/**
 * Poll one operation until it reaches a terminal status. onOperation fires
 * for every observed state (including the final one) so the operator card
 * stays live. Resolves with the terminal operation; rejects with an
 * AbortError when the signal fires.
 */
export const pollRunAnalysisOperationUntilTerminal = async ({
  apiBase,
  getAuthHeader,
  operationId,
  signal,
  onOperation,
  pollIntervalMs = SHERLOC_RUN_ANALYSIS_POLL_INTERVAL_MS,
  fetchImpl,
} = {}) => {
  for (;;) {
    if (signal?.aborted) throw buildAbortError();
    let observed = null;
    try {
      const { operation } = await getRunAnalysisOperation({
        apiBase,
        getAuthHeader,
        operationId,
        signal,
        fetchImpl,
      });
      observed = operation;
    } catch (error) {
      if (error?.name === "AbortError" || signal?.aborted) throw buildAbortError();
      // Transient poll failures (network blips, 5xx) never kill the
      // lifecycle — the operation row is durable; keep polling.
      if (![401, 403, 404].includes(Number(error?.status))) {
        await sleep(pollIntervalMs, signal);
        continue;
      }
      throw error;
    }
    if (observed) {
      if (typeof onOperation === "function") onOperation(observed);
      if (isRunAnalysisOperationTerminal(observed)) return observed;
    }
    await sleep(pollIntervalMs, signal);
  }
};

// One in-flight orchestration per advertiser per tab: a rapid double-click
// shares the SAME promise (and therefore the same POST), before the server's
// own create-or-reuse even has to arbitrate.
const inFlightByAdvertiser = new Map();

/**
 * The full durable Run Analysis flow: start-or-attach, poll to terminal,
 * validate scope, deliver the legacy sync envelope.
 *
 * Resolves with { analysis, operation, replay } where `analysis` is shaped
 * exactly like the legacy synchronous POST /api/ai/run response body — the
 * caller's existing response processing applies unchanged.
 *
 * Rejects with:
 *  - AbortError when the caller's signal fires;
 *  - a typed error carrying `.operation` for terminal failures, whose
 *    message is the disposition-aware operator copy.
 */
export const runSherlocAnalysisViaDurableOperation = async ({
  apiBase,
  getAuthHeader,
  advertiserId,
  payload,
  expectedContext = null,
  // Reload recovery: attach to an existing operation instead of starting one
  // — the durable row, not this tab, is the source of truth.
  attachOperationId = null,
  signal,
  onOperation,
  pollIntervalMs = SHERLOC_RUN_ANALYSIS_POLL_INTERVAL_MS,
  fetchImpl,
} = {}) => {
  const key = readString(advertiserId);
  const existing = inFlightByAdvertiser.get(key);
  if (existing) return existing;

  const flow = (async () => {
    let started = null;
    let operation = null;
    if (attachOperationId) {
      const { operation: fetched } = await getRunAnalysisOperation({
        apiBase,
        getAuthHeader,
        operationId: attachOperationId,
        signal,
        fetchImpl,
      });
      operation = fetched;
    } else {
      started = await startRunAnalysisOperation({
        apiBase,
        getAuthHeader,
        advertiserId,
        payload,
        signal,
        fetchImpl,
      });
      operation = started.operation;
    }
    if (typeof onOperation === "function" && operation) onOperation(operation);

    if (!operation) {
      throw Object.assign(
        new Error("The Sherloc analysis operation could not be created."),
        { code: "operation_create_failed" }
      );
    }

    if (!isRunAnalysisOperationTerminal(operation)) {
      operation = await pollRunAnalysisOperationUntilTerminal({
        apiBase,
        getAuthHeader,
        operationId: operation.id,
        signal,
        onOperation,
        pollIntervalMs,
        fetchImpl,
      });
    } else if (typeof onOperation === "function") {
      onOperation(operation);
    }

    const status = normalizeRunAnalysisStatus(operation?.status);
    if (status !== SHERLOC_RUN_ANALYSIS_STATUSES.COMPLETED) {
      const error = new Error(describeRunAnalysisTerminalFailure(operation));
      error.code = "run_analysis_operation_failed";
      error.operation = operation;
      error.disposition = resolveRunAnalysisDisposition(operation);
      throw error;
    }

    const analysis = operation?.result?.analysis;
    if (!analysis || typeof analysis !== "object") {
      // A COMPLETED operation without a retrievable output is a contract
      // violation — fail loudly rather than showing a success state with
      // nothing to render.
      const error = new Error(
        "The completed analysis has no retrievable output. Support reference: " +
          getRunAnalysisSupportReference(operation)
      );
      error.code = "run_analysis_output_missing";
      error.operation = operation;
      throw error;
    }

    if (
      expectedContext &&
      !runAnalysisResultMatchesContext(operation, expectedContext)
    ) {
      const error = new Error(
        "The completed analysis belongs to a different campaign or timeframe selection and was not rendered."
      );
      error.code = "run_analysis_context_mismatch";
      error.operation = operation;
      throw error;
    }

    return {
      analysis,
      operation,
      replay: started ? started.outcome === "completed_replay" : false,
      rehydrated: Boolean(attachOperationId),
    };
  })();

  inFlightByAdvertiser.set(key, flow);
  try {
    return await flow;
  } finally {
    inFlightByAdvertiser.delete(key);
  }
};

/**
 * The campaign scope token a hydration answer is FOR.
 *
 * "auto" is the portals' word for "no campaign explicitly selected"; it is a
 * scope of its own, not a wildcard, so it normalizes to the empty string
 * rather than matching whatever campaign happens to be newest.
 */
export const buildRunAnalysisScopeKey = ({ advertiserId, campaignId } = {}) => {
  const advertiser = readString(advertiserId);
  const campaign = readString(campaignId);
  return `${advertiser}::${campaign && campaign !== "auto" ? campaign : ""}`;
};

/**
 * GET /:advertiserId/latest — THE canonical hydration read, shared by Cortex
 * and Quantum.
 *
 * Resolves { scopeKey, latest, active }:
 *   latest  the last COMPLETED analysis for this advertiser AND campaign, or
 *           null when this campaign genuinely has none;
 *   active  the operation currently running for this advertiser, if any, so a
 *           reload mid-run reconnects instead of pretending nothing is
 *           happening.
 *
 * scopeKey is echoed from the server's own answer, which is what makes the
 * campaign-switch race safe: a caller that has moved on to another campaign
 * drops a late answer on identity rather than on arrival order.
 *
 * A missing endpoint (an older backend) resolves to an EMPTY answer rather
 * than throwing. Hydration is additive — it reveals a saved analysis, and its
 * absence must never be the reason a page clears one.
 */
export const getLatestPersistedRunAnalysis = async ({
  apiBase,
  getAuthHeader,
  advertiserId,
  campaignId = null,
  signal,
  fetchImpl,
} = {}) => {
  const campaign = readString(campaignId);
  const query =
    campaign && campaign !== "auto"
      ? `?campaignId=${encodeURIComponent(campaign)}`
      : "";
  const { response, payload } = await operationRequest({
    apiBase,
    getAuthHeader,
    path: `${SHERLOC_RUN_ANALYSIS_OPERATIONS_PATH}/${encodeURIComponent(
      advertiserId
    )}/latest${query}`,
    signal,
    fetchImpl,
  });

  const emptyAnswer = {
    scopeKey: buildRunAnalysisScopeKey({ advertiserId, campaignId }),
    latest: null,
    active: null,
    supported: false,
  };
  if (response.status === 404 || response.status === 501) return emptyAnswer;
  if (!response.ok) throw buildRequestError(response, payload);

  return {
    // Prefer the server's own scope stamp; fall back to what we asked for.
    scopeKey: payload?.scope
      ? buildRunAnalysisScopeKey(payload.scope)
      : emptyAnswer.scopeKey,
    latest: payload?.latest || null,
    active: payload?.active || null,
    supported: true,
  };
};

/** The persisted analysis envelope of a completed operation, or null. */
export const extractRunAnalysisPayload = (operation) => {
  const analysis = operation?.result?.analysis;
  return analysis && typeof analysis === "object" ? analysis : null;
};

/**
 * Provenance for §"do not present an older analysis as though it incorporated
 * newer data": what the saved analysis actually looked at, and when.
 */
export const describeRunAnalysisProvenance = (operation) => {
  if (!operation) return null;
  const analysis = extractRunAnalysisPayload(operation);
  const context = operation?.result?.analysisContext || null;
  return {
    operationId: operation.id || null,
    supportReference: getRunAnalysisSupportReference(operation),
    generatedAt: operation.completedAt || analysis?.generatedAt || null,
    dataThrough:
      readString(
        analysis?.resolved_reporting?.dataThrough ||
          analysis?.resolvedReporting?.dataThrough ||
          analysis?.dataThrough
      ) || null,
    advertiserId: readString(context?.advertiserId) || null,
    campaignId: readString(context?.campaignId) || null,
    structuredOutputHash: operation.structuredOutputHash || null,
  };
};

export const _testResetRunAnalysisInFlight = () => {
  inFlightByAdvertiser.clear();
};
